import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowLeft, ChevronRight, Zap } from 'lucide-react';
import { Button } from './ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

gsap.registerPlugin(ScrollTrigger);

const ElectricalHero = () => {
  const heroRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

      tl.fromTo(
        '.electrical-hero-line',
        { scaleX: 0, opacity: 0 },
        { scaleX: 1, opacity: 1, duration: 1 }
      );

      tl.fromTo(
        '.electrical-hero-breadcrumb',
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6 },
        '-=0.6'
      );

      tl.fromTo(
        '.electrical-hero-title',
        { y: 40, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8 },
        '-=0.4'
      );

      tl.fromTo(
        '.electrical-hero-subtitle',
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.7 },
        '-=0.4'
      );
    }, heroRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative h-[50vh] min-h-[400px] flex items-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="/images/securelec-landscape-lighting.webp"
          alt="Securelec landscape lighting illuminating a garden and pool area"
          className="w-full h-full object-cover object-center"
        />
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/30" />
      </div>

      {/* Grid Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(229,33,51,0.2)_1px,transparent_1px),linear-gradient(90deg,rgba(229,33,51,0.2)_1px,transparent_1px)] bg-[size:60px_60px]" />
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 pt-24 relative z-10">
        <div className="max-w-2xl">
          {/* Breadcrumbs */}
          <nav className="electrical-hero-breadcrumb flex items-center gap-2 text-white/60 text-sm mb-6">
            <a href="/" className="hover:text-white transition-colors">Home</a>
            <ChevronRight className="w-4 h-4" />
            <a href="/services" className="hover:text-white transition-colors">Services</a>
            <ChevronRight className="w-4 h-4" />
            <span className="text-securelec-red">Electrical</span>
          </nav>

          <div
            className="electrical-hero-line w-24 h-1 bg-gradient-to-r from-securelec-red to-securelec-red/50 mb-6 origin-left rounded-full"
            style={{ boxShadow: '0 0 20px rgba(229, 33, 51, 0.6)' }}
          />
          <h1 className="electrical-hero-title text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight drop-shadow-lg">
            Electrical
          </h1>
          <p className="electrical-hero-subtitle text-lg md:text-xl text-white/80 leading-relaxed drop-shadow-md">
            Residential and commercial electrical work delivered by a team you can trust.
          </p>
        </div>
      </div>

      {/* Bottom Gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-securelec-light to-transparent" />
    </section>
  );
};

const ElectricalContent = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.electrical-text',
        { x: -60, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.1,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      gsap.fromTo(
        '.electrical-image-block',
        { x: 60, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 0.8,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
            toggleActions: 'play none none reverse',
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const services = [
    {
      title: "EV Chargers",
      description: "An EV charger installation involves the professional setup of a dedicated Level 2 (AC) charging station at a residential or commercial property. Unlike standard portable chargers that plug into a regular power point, these hard-wired units require a licensed electrician to install a dedicated circuit and safety switches (RCDs) directly into your switchboard. Benefits include significant speed (3 to 10 times faster than a standard outlet), extreme convenience, lower running costs especially with off-peak rates or solar integration, increased property value, and better battery longevity through smart charging."
    },
    {
      title: "Light and Power",
      description: "The 'Light and Power' in your home is more than just switches and sockets—it's the backbone of your daily life. Our services ensure your electrical system is not only safe and compliant but also optimised to lower your energy bills. We offer modern lighting with high-efficiency LED downlights using up to 80% less energy, safety-first power solutions with Type A RCD safety switches and modern circuit breakers, future-proof upgrades including dimmers and sensors, and comprehensive fault-finding and safety audits to identify potential hazards before they become expensive emergencies."
    },
    {
      title: "Switchboard Upgrades",
      description: "A switchboard upgrade is the most critical safety improvement you can make for an older property. It involves replacing outdated porcelain fuses and 'old-school' circuit boards with a modern system designed to handle today's high-power appliances and protect your family from electrical hazards. Modern boards feature RCD Safety Switches that cut power in milliseconds if a fault is detected, preventing fatal electric shocks and electrical fires. An upgrade ensures your power won't trip constantly, meets compliance and insurance requirements, and provides space for solar power meters, smart home hubs, and surge protectors."
    },
    {
      title: "Medical & Dental Practices",
      description: "Medical and dental surgeries require specialised electrical environments to ensure patient safety. We perform AS/NZS 3003 compliant installations for Body Protected and Cardiac Protected electrical areas. This includes specialised earthing, RCD testing, and isolated power supplies designed to protect patients and sensitive medical equipment from even the smallest electrical leakages."
    },
    {
      title: "Home Automation",
      description: "Transform your home into a smart, connected space with our home automation solutions. We integrate lighting control, motorised blinds, and security into one seamless platform you can manage from your phone or voice assistant. Whether it's scheduling lights, setting scenes for entertaining, or automating your daily routines, we design and install systems that make everyday living effortless."
    }
  ];

  return (
    <section ref={sectionRef} className="py-24 bg-securelec-light relative overflow-hidden">
      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-64 bg-gradient-to-b from-transparent via-securelec-red to-transparent" />

      <div className="container mx-auto px-6">
        {/* Back to Services Link */}
        <a 
          href="/what-we-do" 
          className="electrical-text inline-flex items-center gap-2 text-securelec-gray/60 hover:text-securelec-red transition-colors mb-8 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Services
        </a>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Text Content */}
          <div>
            <span className="electrical-text inline-flex items-center gap-2 text-securelec-red text-sm font-semibold tracking-widest uppercase mb-4">
              <Zap className="w-4 h-4" />
              What We Offer
            </span>
            <h2 className="electrical-text text-4xl md:text-5xl font-bold text-securelec-dark mb-6">
              Electrical Services
            </h2>
            <div className="electrical-text w-16 h-1 bg-securelec-red mb-8" />
            
            <p className="electrical-text text-lg text-securelec-gray/80 mb-10 leading-relaxed">
              At Securelec, our electrical services are delivered by fully licensed electrical contractors with extensive hands-on experience. From minor repairs to major commercial installations, we bring the same commitment to safety, compliance, and quality workmanship to every job.
            </p>

            {/* Services Accordion */}
            <Accordion type="single" collapsible className="electrical-text space-y-2">
              {services.map((service, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-white rounded-xl border border-gray-200 px-6 overflow-hidden"
                >
                  <AccordionTrigger className="text-xl font-bold text-securelec-dark hover:text-securelec-red hover:no-underline py-5">
                    {service.title}
                  </AccordionTrigger>
                  <AccordionContent className="text-base text-securelec-gray/70 leading-relaxed pb-5">
                    {service.description}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            {/* Mini CTA */}
            <div className="electrical-text mt-10">
              <Button
                className="bg-securelec-red hover:bg-securelec-red/90 text-white px-8 py-6 text-lg rounded-md font-semibold relative overflow-hidden group transition-all duration-300 shadow-lg hover:shadow-xl"
                onClick={() => window.location.href = '/get-a-quote'}
              >
                <span className="relative z-10 flex items-center gap-2">
                  Get a Quote
                  <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
              </Button>
            </div>
          </div>

          {/* Image */}
          <div className="electrical-image-block relative lg:sticky lg:top-32">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://customer-assets.emergentagent.com/job_d0850d40-f350-4ecf-87ac-4e2d9723d534/artifacts/t5pnxxz0_Securelec%20Electrical%20page.jpg"
                alt="Licensed electrician Carrum switchboard and power installation"
                className="w-full h-[550px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-securelec-dark/20 to-transparent" />
            </div>
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-securelec-red/10 rounded-2xl -z-10" />
            <div className="absolute -top-6 -right-6 w-20 h-20 bg-securelec-red/20 rounded-xl -z-10" />
            
            {/* License Badge */}
            <div className="absolute bottom-6 left-6 bg-white rounded-xl p-4 shadow-lg">
              <span className="text-xs text-securelec-gray/50 block">Licensed Contractor</span>
              <span className="text-lg font-bold text-securelec-red">REC 30321</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export { ElectricalHero, ElectricalContent };
